import { useEffect } from 'react';

type Grecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

/**
 * Loads (or reuses an already-loaded) reCAPTCHA v3 script and resolves once
 * `grecaptcha` is ready to execute. Extracted verbatim from the old
 * `contactUsForm.tsx` — behaviour is unchanged.
 */
const loadRecaptcha = async (siteKey: string | undefined): Promise<Grecaptcha> => {
  if (typeof window === 'undefined') {
    throw new Error('Window is not available');
  }

  const w = window as unknown as { grecaptcha?: Grecaptcha };

  // If already available, resolve when it's ready
  if (w.grecaptcha && typeof w.grecaptcha.execute === 'function') {
    await new Promise<void>((resolve) => w.grecaptcha!.ready(() => resolve()));
    return w.grecaptcha!;
  }

  if (!siteKey) {
    throw new Error('Missing VITE_RECAPTCHA_SITE_KEY');
  }

  // Inject script if not present
  let script = document.getElementById('recaptcha-v3') as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = 'recaptcha-v3';
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  await new Promise<void>((resolve, reject) => {
    const onLoad = () => {
      const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
      if (!g) {
        reject(new Error('grecaptcha unavailable after script load'));
        return;
      }
      g.ready(() => resolve());
    };
    script!.addEventListener('load', onLoad, { once: true });
    script!.addEventListener(
      'error',
      () => reject(new Error('Failed to load reCAPTCHA script')),
      {
        once: true,
      },
    );
    // If it was already loaded before listeners attached
    const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
    if (g?.ready) {
      g.ready(() => resolve());
    }
  });

  return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha as Grecaptcha;
};

/**
 * Preloads reCAPTCHA v3 in the background and exposes `getToken` to fetch an
 * execution token on demand (e.g. on form submit). Keeps `import.meta.env`
 * usage confined to this module so tests can mock the hook instead of
 * parsing that syntax.
 */
const useRecaptcha = (): { getToken: () => Promise<string> } => {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  useEffect(() => {
    // Preload recaptcha in the background
    loadRecaptcha(siteKey).catch(() => {
      // silently ignore here; will show error on submit if needed
    });
    // no cleanup needed; script can remain cached
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getToken = async (): Promise<string> => {
    const grecaptcha = await loadRecaptcha(siteKey);
    return grecaptcha.execute(siteKey!, { action: 'contact_form' });
  };

  return { getToken };
};

export default useRecaptcha;
